/**
 * MitzvotDetailImproved - Vista detallada mejorada de un mandamiento
 * Sistema de tablero dinámico para comparar perspectivas rabínicas
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMitzvahById } from '../../utils/mitzvot-api';
import PerspectivePanel from './PerspectivePanel';
import BiblicalReferencePanel from './BiblicalReferencePanel';
import CategoryBadges from './CategoryBadges';
import Loading from '../Common/Loading';
import { ArrowLeft, Check, X, Users, Plus, Trash2 } from 'lucide-react';

const MitzvotDetailImproved = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [mitzvah, setMitzvah] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Tablero de perspectivas seleccionadas (orden importa)
  const [boardPerspectives, setBoardPerspectives] = useState([]);
  
  // Datos cargados de perspectivas (lazy loading)
  const [loadedPerspectives, setLoadedPerspectives] = useState({});

  /**
   * Cargar datos básicos del mandamiento (sin perspectivas completas)
   */
  useEffect(() => {
    const loadMitzvah = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getMitzvahById(id);
        
        if (response.success) {
          setMitzvah(response.data);
        }
      } catch (err) {
        console.error('Error al cargar mandamiento:', err);
        setError('Error al cargar el mandamiento. Por favor intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadMitzvah();
    }
  }, [id]);

  /**
   * Agregar perspectiva al tablero
   */
  const addPerspectiveToBoard = (perspectiveKey) => {
    if (!boardPerspectives.includes(perspectiveKey)) {
      setBoardPerspectives([...boardPerspectives, perspectiveKey]);
      
      // Marcar como cargada (los datos ya vienen en la respuesta inicial)
      if (!loadedPerspectives[perspectiveKey]) {
        setLoadedPerspectives({
          ...loadedPerspectives,
          [perspectiveKey]: true
        });
      }
    }
  };

  /**
   * Remover perspectiva del tablero
   */
  const removePerspectiveFromBoard = (perspectiveKey) => {
    setBoardPerspectives(boardPerspectives.filter(k => k !== perspectiveKey));
  };

  /**
   * Verificar si una perspectiva tiene datos CON CONTENIDO
   */
  const hasPerspectiveData = (perspectiveKey) => {
    if (!mitzvah) return false;
    
    const dataMap = {
      rambam: mitzvah.rambamPerspectives,
      ramban: mitzvah.rambanPerspectives,
      chinuch: mitzvah.chinuchPerspectives,
      semag: mitzvah.semagPerspectives,
      bahag: mitzvah.bahagPerspectives,
      hebrewRoots: mitzvah.hebrewRootsPerspectives
    };
    
    const data = dataMap[perspectiveKey];
    if (!data || data.length === 0) return false;
    
    // Para Ramban y Rambam, verificar que tengan originalText con contenido
    if (perspectiveKey === 'ramban' || perspectiveKey === 'rambam') {
      return data.some(item => item.originalText && item.originalText.trim().length > 0);
    }
    
    // Para SEMAG, verificar semagTitle
    if (perspectiveKey === 'semag') {
      return data.some(item => item.semagTitle && item.semagTitle.trim().length > 0);
    }
    
    // Para BAHAG, verificar mitzvahTitle
    if (perspectiveKey === 'bahag') {
      return data.some(item => item.mitzvahTitle && item.mitzvahTitle.trim().length > 0);
    }
    
    // Para Hebrew Roots, verificar mandamiento
    if (perspectiveKey === 'hebrewRoots') {
      return data.some(item => item.mandamiento && item.mandamiento.trim().length > 0);
    }
    
    // Para Chinuch, verificar originalText o mitzvahTitleEs
    if (perspectiveKey === 'chinuch') {
      return data.some(item => 
        (item.originalText && item.originalText.trim().length > 0) ||
        (item.mitzvahTitleEs && item.mitzvahTitleEs.trim().length > 0)
      );
    }
    
    // Por defecto, si hay datos, asumimos que están OK
    return true;
  };

  if (loading) {
    return <Loading message="Cargando mandamiento..." />;
  }

  if (error || !mitzvah) {
    return (
      <div className="container mx-auto px-4 py-6">
        <button
          onClick={() => navigate('/mandamientos')}
          className="mb-4 flex items-center gap-2 text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a la lista
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'Mandamiento no encontrado'}
        </div>
      </div>
    );
  }

  // Determinar tipo
  const isPositive = mitzvah.asehOrLoSaseh === 'A';
  const typeColor = isPositive ? 'text-green-600' : 'text-red-600';
  const typeBg = isPositive ? 'bg-green-50' : 'bg-red-50';
  const typeIcon = isPositive ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />;

  // Configuración de perspectivas disponibles
  const availablePerspectives = [
    {
      key: 'rambam',
      name: 'Rambam',
      fullName: 'Maimónides',
      hasData: hasPerspectiveData('rambam'),
      color: 'blue',
      colorClasses: {
        button: 'bg-blue-600 hover:bg-blue-700',
        buttonDisabled: 'bg-gray-300 cursor-not-allowed',
        border: 'border-blue-300',
        bg: 'bg-blue-50'
      }
    },
    {
      key: 'ramban',
      name: 'Ramban',
      fullName: 'Najmánides',
      hasData: hasPerspectiveData('ramban'),
      color: 'purple',
      colorClasses: {
        button: 'bg-purple-600 hover:bg-purple-700',
        buttonDisabled: 'bg-gray-300 cursor-not-allowed',
        border: 'border-purple-300',
        bg: 'bg-purple-50'
      }
    },
    {
      key: 'chinuch',
      name: 'Chinuch',
      fullName: 'Sefer HaChinuch',
      hasData: hasPerspectiveData('chinuch'),
      color: 'indigo',
      colorClasses: {
        button: 'bg-indigo-600 hover:bg-indigo-700',
        buttonDisabled: 'bg-gray-300 cursor-not-allowed',
        border: 'border-indigo-300',
        bg: 'bg-indigo-50'
      }
    },
    {
      key: 'semag',
      name: 'SEMAG',
      fullName: 'Sefer Mitzvot Gadol',
      hasData: hasPerspectiveData('semag'),
      color: 'pink',
      colorClasses: {
        button: 'bg-pink-600 hover:bg-pink-700',
        buttonDisabled: 'bg-gray-300 cursor-not-allowed',
        border: 'border-pink-300',
        bg: 'bg-pink-50'
      }
    },
    {
      key: 'bahag',
      name: 'BAHAG',
      fullName: 'Baal Halachot Gedolot',
      hasData: hasPerspectiveData('bahag'),
      color: 'orange',
      colorClasses: {
        button: 'bg-orange-600 hover:bg-orange-700',
        buttonDisabled: 'bg-gray-300 cursor-not-allowed',
        border: 'border-orange-300',
        bg: 'bg-orange-50'
      }
    },
    {
      key: 'hebrewRoots',
      name: 'Raíces Hebreas',
      fullName: 'Mesianismo',
      hasData: hasPerspectiveData('hebrewRoots'),
      color: 'green',
      colorClasses: {
        button: 'bg-green-600 hover:bg-green-700',
        buttonDisabled: 'bg-gray-300 cursor-not-allowed',
        border: 'border-green-300',
        bg: 'bg-green-50'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header fijo */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/mandamientos')}
            className="mb-3 flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a la lista
          </button>
          
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              {/* Número del mandamiento */}
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                <span className="text-3xl font-bold text-white">
                  {mitzvah.mitzvahNumber}
                </span>
              </div>

              {/* Información */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${typeBg} ${typeColor}`}>
                    {typeIcon}
                    {isPositive ? 'Mandamiento de Hacer (Asé)' : 'Mandamiento de No Hacer (Lo Tasé)'}
                  </span>
                </div>
                
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {mitzvah.mitzvahNameEs || mitzvah.mitzvahNameEn}
                </h1>
                
                {mitzvah.mitzvahName && (
                  <p className="text-xl text-gray-700 font-hebrew mb-1" dir="rtl">
                    {mitzvah.mitzvahName}
                  </p>
                )}
                
                {mitzvah.mitzvahNameEn && mitzvah.mitzvahNameEs && (
                  <p className="text-sm text-gray-600">
                    {mitzvah.mitzvahNameEn}
                  </p>
                )}
              </div>
            </div>

            {/* Estadísticas */}
            <div className="text-right">
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {availablePerspectives.filter(p => p.hasData).length} perspectivas disponibles
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="container mx-auto px-4 py-6">
        {/* Panel de referencia bíblica */}
        <BiblicalReferencePanel mitzvah={mitzvah} />

        {/* Selector de perspectivas - Botones con (+) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Agregar Perspectivas al Tablero de Comparación
          </h2>
          
          <div className="flex flex-wrap gap-3">
            {availablePerspectives.map((perspective) => {
              const isInBoard = boardPerspectives.includes(perspective.key);
              const isDisabled = !perspective.hasData;
              
              return (
                <button
                  key={perspective.key}
                  onClick={() => {
                    if (!isDisabled && !isInBoard) {
                      addPerspectiveToBoard(perspective.key);
                    }
                  }}
                  disabled={isDisabled || isInBoard}
                  className={`
                    flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-white
                    transition-all duration-200 transform
                    ${isDisabled 
                      ? perspective.colorClasses.buttonDisabled 
                      : isInBoard
                        ? 'bg-gray-400 cursor-default'
                        : `${perspective.colorClasses.button} hover:scale-105 shadow-md hover:shadow-lg`
                    }
                  `}
                  title={
                    isDisabled 
                      ? 'No hay datos disponibles para esta perspectiva'
                      : isInBoard
                        ? 'Ya está en el tablero'
                        : `Agregar ${perspective.fullName} al tablero`
                  }
                >
                  {!isInBoard && !isDisabled && <Plus className="w-5 h-5" />}
                  <span>{perspective.name}</span>
                  {isInBoard && <Check className="w-5 h-5" />}
                </button>
              );
            })}
          </div>

          {boardPerspectives.length === 0 && (
            <p className="mt-4 text-sm text-gray-600 italic">
              Haz clic en los botones de arriba para agregar perspectivas al tablero de comparación
            </p>
          )}
        </div>

        {/* Tablero de perspectivas */}
        {boardPerspectives.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                Tablero de Comparación
              </h2>
              <span className="text-sm text-gray-600">
                {boardPerspectives.length} {boardPerspectives.length === 1 ? 'perspectiva' : 'perspectivas'} en el tablero
              </span>
            </div>

            {/* Grid dinámico de perspectivas */}
            <div className={`
              grid gap-6
              ${boardPerspectives.length === 1 ? 'grid-cols-1' : ''}
              ${boardPerspectives.length === 2 ? 'grid-cols-1 lg:grid-cols-2' : ''}
              ${boardPerspectives.length >= 3 ? 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3' : ''}
            `}>
              {boardPerspectives.map((perspectiveKey) => {
                const perspectiveConfig = availablePerspectives.find(p => p.key === perspectiveKey);
                
                return (
                  <PerspectivePanel
                    key={perspectiveKey}
                    perspectiveKey={perspectiveKey}
                    perspectiveConfig={perspectiveConfig}
                    mitzvah={mitzvah}
                    onRemove={() => removePerspectiveFromBoard(perspectiveKey)}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MitzvotDetailImproved;

